import { useCallback, useEffect, useRef, useState } from "react";

import API_BASE, { protectedApiFetch } from "../../../api";
import { getActiveAuthTokenSource } from "../../../auth/bootstrapTokens";
import {
  getOrCreatePostSubmissionRequestId,
  resetPostSubmissionRequestId,
} from "../../../utils/postSubmissionRequestId";
import {
  requestCatchSubmitRefresh,
  setCatchSubmitActive,
} from "../../../app/catchSubmitGate";
import { getFishSpeciesValidationErrorKey } from "../../../utils/fishSpeciesValidation";
import { buildCatchPayload } from "./CatchFormValidation";
import {
  CATCH_ANDROID_PICKER_UNREADABLE_ERROR_CODE,
  CATCH_ANDROID_PICKER_UNREADABLE_ERROR_MESSAGE,
  CATCH_IMAGE_NORMALIZATION_ERROR_CODE,
  CATCH_MULTIPART_BUILD_ERROR_CODE,
  CATCH_MULTIPART_BUILD_ERROR_MESSAGE,
  inferSelectedMediaType,
  isCatchMediaClientError,
  resolveCatchMediaMessage,
} from "./catchMediaConstants";
import { buildMultipartMediaPart } from "./catchMediaPipeline";
import {
  CATCH_SUBMIT_SLOW_NOTICE_MS,
  CATCH_SUBMIT_TIMEOUT_MS,
  SUBMIT_PHASES,
  buildInitialTransaction,
  computeSubmitReady,
  getAuthSnapshot,
  logCatchTx,
} from "./catchSubmitUtils";

export default function useCatchSubmitFlow({
  t,
  onClose,
  onAddPost,
  onSubmittingChange,
  selectionIdRef,
  getMediaApi,
  getFormSnapshot,
  requestLocationConsent,
  resetFormState,
  playPostSuccessSound,
}) {
  const [submitPhase, setSubmitPhaseState] = useState(SUBMIT_PHASES.IDLE);
  const [submitStatusMessage, setSubmitStatusMessage] = useState("");
  const [submitStatusTone, setSubmitStatusTone] = useState("info");
  const submitAttemptCounterRef = useRef(0);
  const submitPhaseRef = useRef(SUBMIT_PHASES.IDLE);
  const [submitTransactionState, setSubmitTransactionState] = useState(() =>
    buildInitialTransaction()
  );
  const submitTransactionRef = useRef(submitTransactionState);
  const submitSlowNoticeRef = useRef(null);
  const submitTimeoutRef = useRef(null);
  const clientRequestIdRef = useRef("");
  const isSubmitting = submitTransactionState.status === "submitting";

  useEffect(() => {
    submitTransactionRef.current = submitTransactionState;
  }, [submitTransactionState]);

  const setSubmitTransaction = useCallback(
    (next, options = {}) => {
      const { attemptId, allowStale = false, reason = "" } = options;
      const current = submitTransactionRef.current || buildInitialTransaction();

      if (!allowStale && attemptId != null && current.attemptId !== attemptId) {
        logCatchTx("stale_callback_ignored", {
          attemptId,
          reason: reason || "transaction_mismatch",
          currentAttemptId: current.attemptId ?? null,
        });
        return false;
      }

      const nextValue = typeof next === "function" ? next(current) : next;
      const updated = {
        ...current,
        ...nextValue,
      };

      submitTransactionRef.current = updated;
      setSubmitTransactionState(updated);

      const wasSubmitting = current.status === "submitting";
      const isSubmittingNow = updated.status === "submitting";
      if (wasSubmitting !== isSubmittingNow) {
        onSubmittingChange?.(isSubmittingNow);
        setCatchSubmitActive(isSubmittingNow, {
          attemptId: updated.attemptId ?? null,
          reason: reason || "transaction_update",
        });
      }

      return true;
    },
    [onSubmittingChange]
  );

  const resetSubmitTransaction = useCallback((options = {}) => {
    const { attemptId, reason = "" } = options;
    const current = submitTransactionRef.current || buildInitialTransaction();
    const nextAttemptId =
      typeof attemptId === "number" ? attemptId : current.attemptId || 0;
    const updated = buildInitialTransaction(nextAttemptId);

    submitTransactionRef.current = updated;
    setSubmitTransactionState(updated);
    void reason;

    return true;
  }, []);

  const setSubmitPhase = useCallback((nextPhase, options = {}) => {
    const { force = false } = options;
    const currentPhase = submitPhaseRef.current;
    const txStatus = submitTransactionRef.current?.status;
    const isTxLocked = txStatus === "submitting" || txStatus === "success";

    if (
      !force &&
      (currentPhase === SUBMIT_PHASES.SUBMITTING || isTxLocked) &&
      nextPhase !== SUBMIT_PHASES.SUBMITTING &&
      nextPhase !== SUBMIT_PHASES.SUCCEEDED &&
      nextPhase !== SUBMIT_PHASES.FAILED
    ) {
      return false;
    }

    if (currentPhase === nextPhase) {
      return false;
    }

    submitPhaseRef.current = nextPhase;
    setSubmitPhaseState(nextPhase);

    return true;
  }, []);

  const clearSubmitTimers = useCallback(() => {
    if (submitSlowNoticeRef.current) {
      clearTimeout(submitSlowNoticeRef.current);
      submitSlowNoticeRef.current = null;
    }
    if (submitTimeoutRef.current) {
      clearTimeout(submitTimeoutRef.current);
      submitTimeoutRef.current = null;
    }
  }, []);

  const setSubmitStatus = useCallback((message, tone = "info") => {
    setSubmitStatusMessage(message || "");
    setSubmitStatusTone(tone);
  }, []);

  const resetSubmissionState = useCallback(
    (options = {}) => {
      const {
        clearStatusMessage = false,
        nextPhase = SUBMIT_PHASES.IDLE,
        force = false,
        reason = "reset",
      } = options;

      const currentTx = submitTransactionRef.current || buildInitialTransaction();

      if (!force && currentTx.status === "submitting") {
        return false;
      }

      clearSubmitTimers();
      setSubmitPhase(nextPhase, { force: true });

      const mediaApi = getMediaApi?.();
      if (mediaApi?.uploadAssetRef?.current?.snapshotStatus === "pending") {
        console.warn("[CATCH_IMG_DEBUG]", {
          stage: "snapshot_pending_state_reset",
          selectionId: mediaApi.uploadAssetRef.current.selectionId ?? null,
          reason,
          nextPhase,
        });
      }

      if (nextPhase === SUBMIT_PHASES.IDLE) {
        resetSubmitTransaction({
          attemptId: currentTx.attemptId,
          reason,
        });
      }

      if (clearStatusMessage) {
        setSubmitStatus("", "info");
      }

      logCatchTx("state_reset", {
        attemptId: currentTx.attemptId ?? null,
        reason,
      });

      return true;
    },
    [clearSubmitTimers, getMediaApi, resetSubmitTransaction, setSubmitPhase, setSubmitStatus]
  );

  useEffect(() => {
    resetSubmissionState({ force: true, nextPhase: SUBMIT_PHASES.IDLE });

    return () => {
      resetSubmissionState({ force: false, nextPhase: SUBMIT_PHASES.IDLE });
    };
  }, [resetSubmissionState]);

  const handlePostSubmit = useCallback(
    async (options = {}) => {
      const activeTx = submitTransactionRef.current || buildInitialTransaction();

      if (activeTx.status === "submitting") {
        const message = t("catch_submit_in_progress", {
          defaultValue: "Upload already in progress.",
        });
        setSubmitStatus(message, "info");
        logCatchTx("request_failed", {
          attemptId: activeTx.attemptId ?? null,
          reason: "submit_lock",
        });
        return;
      }

      if (activeTx.status === "success" && activeTx.closeRequested) {
        logCatchTx("success_already_handled", {
          attemptId: activeTx.attemptId ?? null,
          reason: "submit_after_close",
        });
        return;
      }

      const submitAttemptId = submitAttemptCounterRef.current + 1;
      submitAttemptCounterRef.current = submitAttemptId;

      const authSnapshot = getAuthSnapshot();
      const mediaApi = getMediaApi?.();
      const currentMedia = mediaApi?.mediaStateRef?.current || mediaApi?.mediaState || {};
      const selectionId = selectionIdRef.current;
      const uploadFile = currentMedia.uploadFile || null;
      const blockingError = currentMedia.blockingError || "";
      const submitReadySnapshot = computeSubmitReady(uploadFile, blockingError);
      const clientRequestId = clientRequestIdRef.current || "";
      const uploadAsset = mediaApi?.uploadAssetRef?.current;
      const uploadMediaType =
        currentMedia.mediaType ||
        inferSelectedMediaType(
          uploadFile || uploadAsset?.snapshotFile || uploadAsset?.originalFile,
          "image"
        );
      const formSnapshot = getFormSnapshot?.() || {};

      if (!uploadAsset?.snapshotFile && !uploadAsset?.originalFile) {
        console.warn("[CATCH_IMG_DEBUG]", {
          stage: "submit_blocked_missing_upload_source",
          selectionId,
          snapshotStatus: uploadAsset?.snapshotStatus || "missing",
          hasSnapshotFile: Boolean(uploadAsset?.snapshotFile),
          hasOriginalFile: Boolean(uploadAsset?.originalFile),
        });
        const message = blockingError
          ? resolveCatchMediaMessage(blockingError, t)
          : t("catch_media_required");
        setSubmitStatus(message, "error");
        mediaApi?.updateMediaState?.(selectionId, (prev) => ({
          ...prev,
          blockingError: blockingError || message,
          submitReady: false,
        }));
        logCatchTx("request_failed", {
          attemptId: submitAttemptId,
          reason: "missing_upload_source",
        });
        return;
      }

      if (
        uploadMediaType === "image" &&
        !uploadAsset?.snapshotFile &&
        uploadAsset?.snapshotStatus !== "pending"
      ) {
        const mediaMessage =
          currentMedia.nonBlockingMessage ||
          CATCH_ANDROID_PICKER_UNREADABLE_ERROR_MESSAGE;
        const message = resolveCatchMediaMessage(mediaMessage, t);
        console.warn("[CATCH_IMG_DEBUG]", {
          stage: "invalid_image_upload_source_blocked",
          selectionId,
          snapshotStatus: uploadAsset?.snapshotStatus || "missing",
          sourceKind: uploadAsset?.originalFile ? "original_file" : "missing",
          reason: "submit_preflight_without_snapshot",
          hasSnapshotFile: Boolean(uploadAsset?.snapshotFile),
          hasOriginalFile: Boolean(uploadAsset?.originalFile),
        });
        mediaApi?.rejectImageSelectionWithoutSnapshot?.(selectionId, {
          snapshotStatus: uploadAsset?.snapshotStatus || "failed",
          failureCode: CATCH_ANDROID_PICKER_UNREADABLE_ERROR_CODE,
          failureReason: "submit_preflight_without_snapshot",
          message: mediaMessage,
          reason: "submit_preflight_without_snapshot",
        });
        setSubmitStatus(message, "error");
        logCatchTx("request_failed", {
          attemptId: submitAttemptId,
          reason: "invalid_image_upload_source",
        });
        return;
      }

      let snapshot = {
        submitAttemptId,
        selectionId,
        clientRequestId,
        uploadFile,
        uploadFilename: currentMedia.uploadFilename,
        uploadMimeType: currentMedia.uploadMimeType,
        mediaType: currentMedia.mediaType,
        selectedFile: currentMedia.selectedFile,
        originalFile: currentMedia.originalFile,
        snapshotFile: currentMedia.snapshotFile,
        snapshotStatus: currentMedia.snapshotStatus,
        blockingError,
        submitReady: submitReadySnapshot,
        ...formSnapshot,
      };

      let endpoint = "posts/catch";
      let isTripCatch = false;

      setSubmitStatus("", "info");

      const fishSpeciesErrorKey = getFishSpeciesValidationErrorKey(
        snapshot.fishInput
      );
      if (fishSpeciesErrorKey) {
        const message = t(fishSpeciesErrorKey, {
          defaultValue:
            "Please enter the fish name using English/Latin letters.",
        });
        setSubmitStatus(message, "error");
        logCatchTx("request_failed", {
          attemptId: submitAttemptId,
          reason: "invalid_fish_species",
        });
        return;
      }

      if (!snapshot.uploadFile || !snapshot.submitReady) {
        const message = snapshot.blockingError
          ? resolveCatchMediaMessage(snapshot.blockingError, t)
          : t("catch_media_required");
        logCatchTx("request_failed", {
          attemptId: submitAttemptId,
          reason: "missing_media",
        });
        setSubmitStatus(message, "error");
        alert(message);
        return;
      }

      if (
        !options.skipLocationConsent &&
        snapshot.coordinates &&
        snapshot.isLocationPublic &&
        snapshot.requiresLocationSharingConsent
      ) {
        const message = t("catch_location_consent_required", {
          defaultValue: "Please confirm location sharing to continue.",
        });
        logCatchTx("request_failed", {
          attemptId: submitAttemptId,
          reason: "location_consent",
        });
        requestLocationConsent?.();
        setSubmitStatus(message, "info");
        return;
      }

      snapshot = {
        ...snapshot,
        clientRequestId: getOrCreatePostSubmissionRequestId(clientRequestIdRef),
      };

      setSubmitTransaction(
        {
          ...buildInitialTransaction(submitAttemptId),
          status: "submitting",
          requestStartedAt: Date.now(),
          clientRequestId: snapshot.clientRequestId,
          tokenSource: authSnapshot.source,
        },
        { attemptId: submitAttemptId, allowStale: true, reason: "started" }
      );

      setSubmitPhase(SUBMIT_PHASES.SUBMITTING, { force: true });
      logCatchTx("started", {
        attemptId: submitAttemptId,
        clientRequestId: snapshot.clientRequestId,
      });

      const isCurrentAttempt = () =>
        submitTransactionRef.current?.attemptId === submitAttemptId;
      const markTransaction = (updates, reason) =>
        setSubmitTransaction(updates, { attemptId: submitAttemptId, reason });
      const maybeLogTokenChange = (phase) => {
        if (typeof window === "undefined") {
          return;
        }
        const currentSource = getActiveAuthTokenSource(window) || "";
        const lastSource =
          submitTransactionRef.current?.tokenSource || authSnapshot.source || "";
        if (currentSource && currentSource !== lastSource) {
          logCatchTx("token_changed_mid_submit", {
            attemptId: submitAttemptId,
            from: lastSource,
            to: currentSource,
            phase,
          });
          markTransaction({ tokenSource: currentSource }, "token_changed");
        }
      };

      const handleSubmitSuccess = (savedRecord) => {
        if (!isCurrentAttempt()) {
          logCatchTx("stale_callback_ignored", {
            attemptId: submitAttemptId,
            reason: "success_stale",
            endpoint,
          });
          return false;
        }

        const currentTx = submitTransactionRef.current;
        if (currentTx?.status === "success") {
          logCatchTx("success_already_handled", {
            attemptId: submitAttemptId,
            endpoint,
          });
          return false;
        }

        markTransaction(
          {
            status: "success",
            responseReceivedAt: currentTx?.responseReceivedAt || Date.now(),
            postId: savedRecord?.id ?? savedRecord?.catchId ?? null,
          },
          "request_succeeded"
        );
        setSubmitPhase(SUBMIT_PHASES.SUCCEEDED, { force: true });

        playPostSuccessSound?.();
        if (savedRecord && !isTripCatch) {
          onAddPost?.(savedRecord, { skipRefreshEvents: true });
        }

        resetFormState?.();
        resetPostSubmissionRequestId(clientRequestIdRef);
        setSubmitStatus("", "info");

        const updatedTx = submitTransactionRef.current || currentTx;
        if (!updatedTx?.closeRequested) {
          markTransaction({ closeRequested: true }, "modal_close");
          onClose?.({ force: true, reason: "submit_success" });
        } else {
          logCatchTx("success_already_handled", {
            attemptId: submitAttemptId,
            reason: "modal_close",
          });
        }

        if (!updatedTx?.refreshRequested) {
          markTransaction({ refreshRequested: true }, "refresh_queued");
          requestCatchSubmitRefresh({
            attemptId: submitAttemptId,
            endpoint,
          });
        } else {
          logCatchTx("success_already_handled", {
            attemptId: submitAttemptId,
            reason: "refresh_queued",
          });
        }

        resetSubmissionState({
          clearStatusMessage: true,
          nextPhase: SUBMIT_PHASES.IDLE,
          force: true,
          reason: "submit_success",
        });

        return true;
      };

      const catchData = buildCatchPayload({
        fishInput: snapshot.fishInput,
        waterType: snapshot.waterType,
        wayOfFishing: snapshot.wayOfFishing,
        typeOfFishing: snapshot.typeOfFishing,
        style: snapshot.style,
        customStyle: snapshot.customStyle,
        technique: snapshot.technique,
        rod: snapshot.rod,
        reel: snapshot.reel,
        braid: snapshot.braid,
        leader: snapshot.leader,
        lure: snapshot.lure,
        lureSize: snapshot.lureSize,
        lureColor: snapshot.lureColor,
        hookBrand: snapshot.hookBrand,
        hookSpec: snapshot.hookSpec,
        knotBraidToLeader: snapshot.knotBraidToLeader,
        knotLeaderToLure: snapshot.knotLeaderToLure,
        catchAndRelease: snapshot.catchAndRelease,
        coordinates: snapshot.coordinates,
        isLocationPublic: snapshot.isLocationPublic,
        location: snapshot.location,
        exifDate: snapshot.exifDate,
        exifTime: snapshot.exifTime,
        rating: snapshot.rating,
        feedback: snapshot.feedback,
        caption: snapshot.caption,
        sizeUnit: snapshot.sizeUnit,
        size: snapshot.size,
        sizeFeet: snapshot.sizeFeet,
        sizeInches: snapshot.sizeInches,
        weightUnit: snapshot.weightUnit,
        weight: snapshot.weight,
        weightLb: snapshot.weightLb,
      });

      let multipartMedia = null;
      let didTimeout = false;
      let didStartFetch = false;
      let hasSpecificSubmitError = false;
      const abortController =
        typeof AbortController === "function" ? new AbortController() : null;

      isTripCatch =
        snapshot.tripId !== undefined &&
        snapshot.tripId !== null &&
        snapshot.tripId !== "";
      endpoint = isTripCatch ? "trips/catches" : "posts/catch";

      const startSubmitTimers = () => {
        clearSubmitTimers();
        submitSlowNoticeRef.current = setTimeout(() => {
          if (!isCurrentAttempt()) {
            return;
          }
          setSubmitStatus(
            t("catch_submit_uploading_slow", {
              defaultValue: "Upload still in progress...",
            }),
            "info"
          );
        }, CATCH_SUBMIT_SLOW_NOTICE_MS);
        submitTimeoutRef.current = setTimeout(() => {
          if (!isCurrentAttempt()) {
            return;
          }
          didTimeout = true;
          setSubmitStatus(
            t("catch_submit_timeout", {
              defaultValue: "Upload timed out, please retry.",
            }),
            "error"
          );
          if (abortController) {
            abortController.abort();
          }
        }, CATCH_SUBMIT_TIMEOUT_MS);
      };

      try {
        multipartMedia = buildMultipartMediaPart(snapshot, uploadAsset);
      } catch (error) {
        setSubmitStatus(
          resolveCatchMediaMessage(
            error?.message || CATCH_MULTIPART_BUILD_ERROR_MESSAGE,
            t
          ),
          "error"
        );
        markTransaction(
          {
            status: "error",
            responseReceivedAt: null,
          },
          "multipart_failed"
        );
        setSubmitPhase(SUBMIT_PHASES.FAILED, { force: true });
        resetSubmissionState({
          nextPhase: SUBMIT_PHASES.FAILED,
          force: true,
          reason: "multipart_failed",
        });
        logCatchTx("request_failed", {
          attemptId: submitAttemptId,
          reason: "multipart_build_failed",
        });
        return;
      }

      try {
        const form = new FormData();
        if (isTripCatch) {
          form.append("catchData", JSON.stringify(catchData));
        } else {
          form.append("caption", snapshot.caption);
          form.append("catchData", JSON.stringify(catchData));
          form.append("clientRequestId", snapshot.clientRequestId);
        }

        if (multipartMedia?.blob) {
          const uploadFileRef =
            uploadAsset?.snapshotFile || uploadAsset?.originalFile || null;
          console.log("[CATCH_IMG_DEBUG]", {
            stage: "upload_append_source",
            selectionId: snapshot.selectionId ?? selectionIdRef.current,
            sourceKind: uploadAsset?.snapshotFile ? "snapshot_file" : "original_file",
            name: typeof uploadFileRef?.name === "string" ? uploadFileRef.name : "",
            size: Number.isFinite(uploadFileRef?.size) ? uploadFileRef.size : null,
            type: typeof uploadFileRef?.type === "string" ? uploadFileRef.type : "",
          });
          form.append("media", multipartMedia.blob, multipartMedia.filename);
        }

        startSubmitTimers();
        didStartFetch = true;
        markTransaction({ requestStartedAt: Date.now() }, "request_sent");
        logCatchTx("request_sent", {
          attemptId: submitAttemptId,
          endpoint,
        });
        maybeLogTokenChange("before_send");

        const requestOptions = {
          method: "POST",
          body: form,
          signal: abortController?.signal,
        };

        const res = await protectedApiFetch(
          isTripCatch
            ? `${API_BASE}/trips/${snapshot.tripId}/catches`
            : `${API_BASE}/posts/catch`,
          requestOptions
        );

        markTransaction({ responseReceivedAt: Date.now() }, "response_received");
        maybeLogTokenChange("after_response");

        if (!res.ok) {
          logCatchTx("request_failed", {
            attemptId: submitAttemptId,
            endpoint,
            status: res.status,
            errorCategory:
              res.status === 401
                ? "session_expired"
                : res.status === 403
                  ? "permission_denied"
                  : "http_error",
          });
          const responseError = new Error(
            isTripCatch
              ? "Failed to save catch to backend"
              : "Failed to upload post"
          );
          responseError.status = res.status;
          throw responseError;
        }

        logCatchTx("request_succeeded", {
          attemptId: submitAttemptId,
          endpoint,
          status: res.status,
        });

        let savedRecord = null;
        try {
          savedRecord = await res.json();
        } catch {
          savedRecord = null;
        }

        if (!isCurrentAttempt()) {
          logCatchTx("stale_callback_ignored", {
            attemptId: submitAttemptId,
            reason: "success_stale",
            endpoint,
          });
          return;
        }

        handleSubmitSuccess(savedRecord);
      } catch (error) {
        const aborted =
          error?.name === "AbortError" || abortController?.signal?.aborted;

        if (!isCurrentAttempt()) {
          logCatchTx("stale_callback_ignored", {
            attemptId: submitAttemptId,
            reason: "error_stale",
            endpoint,
          });
          return;
        }

        if (submitTransactionRef.current?.status === "success") {
          logCatchTx("success_already_handled", {
            attemptId: submitAttemptId,
            reason: "error_after_success",
            endpoint,
          });
          return;
        }

        markTransaction(
          {
            status: "error",
            responseReceivedAt: didStartFetch && !aborted ? Date.now() : null,
          },
          "request_failed"
        );

        if (
          isCatchMediaClientError(error, CATCH_IMAGE_NORMALIZATION_ERROR_CODE) ||
          isCatchMediaClientError(error, CATCH_MULTIPART_BUILD_ERROR_CODE)
        ) {
          hasSpecificSubmitError = true;
          if (isCatchMediaClientError(error, CATCH_IMAGE_NORMALIZATION_ERROR_CODE)) {
            const message = resolveCatchMediaMessage(error.message, t);
            setSubmitStatus(message, "error");
            mediaApi?.updateMediaState?.(selectionId, (prev) => ({
              ...prev,
              blockingError: error.message,
              submitReady: computeSubmitReady(prev.uploadFile, error.message),
            }));
            alert(message);
          }
        }

        if (aborted && !didTimeout) {
          setSubmitStatus(
            t("catch_submit_aborted", {
              defaultValue: "Upload was interrupted. Please try again.",
            }),
            "error"
          );
        }

        if (!aborted && !didTimeout) {
          const message =
            error?.status === 401
              ? t("catch_submit_session_expired", {
                  defaultValue:
                    "Your session has expired. Please sign in again.",
                })
              : error?.status === 403
                ? t("catch_submit_permission_denied", {
                    defaultValue:
                      "You do not have permission to create this catch post.",
                  })
                : error?.status
                  ? t("catch_submit_server_failed", {
                      defaultValue:
                        "The catch could not be uploaded. Please try again.",
                    })
                  : isTripCatch
                    ? t("catch_save_failed")
                    : t("catch_post_upload_failed");
          if (!hasSpecificSubmitError) {
            setSubmitStatus(message, "error");
            alert(message);
          }
        }

        setSubmitPhase(SUBMIT_PHASES.FAILED, { force: true });
        resetSubmissionState({
          nextPhase: SUBMIT_PHASES.FAILED,
          force: true,
          reason: "request_failed",
        });

        logCatchTx("request_failed", {
          attemptId: submitAttemptId,
          endpoint,
          aborted,
          timedOut: didTimeout,
          errorMessage: error?.message,
        });
      }
    },
    [
      clearSubmitTimers,
      getFormSnapshot,
      getMediaApi,
      onAddPost,
      onClose,
      playPostSuccessSound,
      requestLocationConsent,
      resetFormState,
      resetSubmissionState,
      selectionIdRef,
      setSubmitPhase,
      setSubmitStatus,
      setSubmitTransaction,
      t,
    ]
  );

  const handleCancel = useCallback(() => {
    onClose?.({
      reason: "user_cancel",
      onBeforeClose: () => {
        resetPostSubmissionRequestId(clientRequestIdRef);
        resetSubmissionState({
          clearStatusMessage: true,
          nextPhase: SUBMIT_PHASES.IDLE,
          force: true,
          reason: "user_cancel",
        });
        resetFormState?.();
      },
    });
  }, [onClose, resetFormState, resetSubmissionState]);

  return {
    submitPhase,
    submitStatusMessage,
    submitStatusTone,
    isSubmitting,
    submitTransactionRef,
    setSubmitPhase,
    setSubmitStatus,
    handlePostSubmit,
    handleCancel,
  };
}
